import { useState } from 'react'

import { Heading } from 'almanac-ui/Heading/Heading'
import { Modal, StackModalProps } from 'almanac-ui/Modal'
import Styles from './NewSnippetModal.module.scss'

import { CreateKnowledgeBaseSnippetPayload } from 'api/clients/KnowledgeBaseClient'
import { useCreateSnippet } from 'hooks/useSnippets'
import { KnowledgeBaseSnippetKind } from 'types'
import { SnippetForm } from '../SnippetForm/SnippetForm'
import { SnippetKindLayout } from 'screens/BrandKits/utils'

export interface NewSnippetModalProps extends StackModalProps {
  content?: string
  onClose?: () => void
  onSave?: (payload: CreateKnowledgeBaseSnippetPayload) => void
  kinds?: KnowledgeBaseSnippetKind[]
}

export const NewSnippetModal = ({
  close,
  onClose,
  onSave,
  content = '',
  kinds = Object.values(KnowledgeBaseSnippetKind),
}: NewSnippetModalProps) => {
  const [selectedType, setSelectedType] = useState<KnowledgeBaseSnippetKind | undefined>(
    kinds.length === 1 ? kinds[0] : undefined,
  )
  const createSnippet = useCreateSnippet()
  const closeModal = () => {
    close()
    onClose?.()
  }
  const onSubmit = async (payload: CreateKnowledgeBaseSnippetPayload) => {
    if (!selectedType || !payload.name || !payload.content) return

    return createSnippet.mutateAsync(payload).then((_res) => {
      onSave?.(payload)
      close()
    })
  }

  const headerText = !!selectedType ? (
    <>Create Snippet</>
  ) : (
    <div className={Styles.header}>
      Select a snippet type to create
      <div className={Styles.subtitle}>
        Snippets are reusable information that you can apply during Blaze’s AI content generation. Begin by selecting a
        type of snippet.
      </div>
    </div>
  )

  const renderTypeSelect = () => {
    return (
      <Modal.Content>
        <div className={Styles.typesContainer}>
          {kinds.map((kind) => (
            <div key={kind} className={Styles.type} onClick={() => setSelectedType(kind)}>
              <div className={Styles.icon}>{SnippetKindLayout[kind].icon()}</div>
              <div>
                <Heading level={4}>{SnippetKindLayout[kind].title}</Heading>
                <div className={Styles.description}>{SnippetKindLayout[kind].description}</div>
              </div>
            </div>
          ))}
        </div>
      </Modal.Content>
    )
  }

  const renderContent = () => {
    if (selectedType)
      return (
        <SnippetForm
          onSecondaryAction={() => setSelectedType(undefined)}
          onSubmit={onSubmit}
          snippet={{ emoji: '', kind: selectedType, name: '', default: false, content, use_count: onSave ? 1 : 0 }}
        />
      )

    return renderTypeSelect()
  }

  return (
    <Modal.Root size="sm" blaze onClose={closeModal} onPressOutside={closeModal}>
      <Modal.Header title={headerText} />
      {renderContent()}
    </Modal.Root>
  )
}
